// Copyright (c) 2026 Goffy. All Rights Reserved.

/**
 * One displayed line of a club's opening hours: the venue it is about, when the line names one
 * ("Klubbhus", "Restaurang"), and the words that follow.
 */
export type OpeningHoursLine = { label: string | null; text: string };

/**
 * A run of words with no digit and no colon, then a colon — "Klubbhus:", "Kansli/Shop:". The
 * digit exclusion is what keeps "06:30" from ever reading as a label.
 */
const LABEL = /^([^\d:]{1,40}?):\s+(.+)$/;

/** Break before a label that is not at the start of the line: up to three words, then ":". */
const BEFORE_LABEL = /\s+((?:[^\s\d:]+\s){0,2}[^\s\d:]+:)(?=\s)/g;

/** Break after a time range — "06:30 – 20:30", "08.00-15.00" — when more words follow it. */
const AFTER_TIME_RANGE = /(\d{1,2}[:.]\d{2}\s*[-–—]\s*\d{1,2}[:.]\d{2})\s+(?=\S)/g;

/**
 * Lays a club's opening hours out in lines, without changing a word of them.
 *
 * The text is verbatim — copied off the club's site, or OpenStreetMap's own grammar — and it
 * stays verbatim: this decides only where one line ends and the next begins. Lines the source
 * already had are kept. Within a line, a new venue ("Restaurang:") starts a new line, and so does
 * whatever follows a completed time range, which is how "Vardagar 08:30 – 17:00 Lör, Sön 08:00 -
 * 15:00" becomes two lines under one label. Nothing here reads the hours: a break after
 * "08:00-20:00" says nothing about whether the club is open, only where the eye should go next.
 */
export function layoutOpeningHours(raw: string): OpeningHoursLine[] {
  return raw
    .split(/\r?\n/)
    .flatMap((stored) =>
      stored.replace(BEFORE_LABEL, '\n$1').replace(AFTER_TIME_RANGE, '$1\n').split('\n'),
    )
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const labelled = LABEL.exec(line);
      return labelled ? { label: labelled[1], text: labelled[2] } : { label: null, text: line };
    });
}
